/**
 * AutoGlow · Client Advisor Dashboard · Sprint T-13.
 *
 * 18 widgets that exclusively CONSUME intelligence from AG-01..AG-06.
 * NO calculations · NO scoring · NO aggregation in React.
 *
 * Route: /centro/advisor/:slug
 *  - slug → /api/public/org/{slug} → org_id + primary taxonomy_code
 *  - org_id → AG-01/AG-02/AG-06 endpoints
 *  - primary code → AG-03 (SEO) and AG-04 (Content) endpoints
 *
 * Tabs persisted in URL via `?tab=`.
 */
import React, { useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Building2, RefreshCw, Sparkles } from "lucide-react";

import {
  QueryGate, WidgetCard, WidgetGrid,
  ScoreGauge, PercentileGauge,
  KpiCard, MetricCard, InsightCard, GrowthPlayCard,
  SeoCard, ContentCard, CompetitorCard,
  FunnelChart, CalendarView, RankedList,
  SectorBars, CitationBadge, BandBadge,
  EmptyState, LoadingState,
} from "../../components/advisor";
import {
  useOrgBySlug, useSuperadminOrgs,
  useBusinessScore, useBusinessSummary, useBusinessReport, useBusinessPriorities, useBusinessRoadmap,
  useGrowthFunnel, useGrowthExperiments, useGrowthPlan, useGrowthKpis, useGrowthForecast,
  useSeoScore, useSeoLinks,
  useContentPreview,
  useCompetitorOpportunities, useCompetitorCandidates, useCompetitorSwot, useCompetitorGap,
  useBenchmark, useSectorIntelligence,
} from "../../hooks/useAdvisor";

const CLIENT_TABS = [
  { id: "resumen", label: "Resumen" },
  { id: "roadmap", label: "Roadmap" },
  { id: "kpis", label: "KPIs" },
  { id: "competencia", label: "Competencia" },
  { id: "seo", label: "SEO" },
  { id: "content", label: "Contenido" },
  { id: "benchmark", label: "Benchmark" },
];

const HORIZONS = ["weekly", "monthly", "quarterly"];

// ---------------------------------------------------------------------------
// Header · Hero with key gauges
// ---------------------------------------------------------------------------

function ClientHero({ org, businessQuery, funnelQuery }) {
  const businessScore = businessQuery.data?.score_global ?? businessQuery.data?.global_score ?? businessQuery.data?.score;
  const funnel = funnelQuery.data;
  const globalFunnel = funnel?.global_funnel_health;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="client-hero">
      <WidgetCard title="Business Score" citations={["AG-01"]} testId="widget-C1-business-gauge">
        {businessQuery.isLoading && <LoadingState />}
        {businessQuery.error && <EmptyState message="Acceso restringido o sin datos." />}
        {businessQuery.data && (
          <ScoreGauge value={businessScore} label="Business" testId="client-business-gauge" />
        )}
      </WidgetCard>
      <WidgetCard title="Growth Score" citations={["AG-02"]} testId="widget-C2-growth-gauge">
        {funnelQuery.isLoading && <LoadingState />}
        {funnelQuery.error && <EmptyState message="Acceso restringido o sin datos." />}
        {funnel && <ScoreGauge value={globalFunnel} label="Funnel Health" testId="client-growth-gauge" />}
      </WidgetCard>
      <WidgetCard title="Funnel" citations={["AG-02"]} testId="widget-C3-funnel-mini">
        {funnelQuery.isLoading && <LoadingState />}
        {funnelQuery.error && <EmptyState message="No se pudo cargar el funnel." />}
        {funnel && (
          <FunnelChart
            stages={funnel.stages}
            global={funnel.global_funnel_health}
            weakest={funnel.weakest_stage}
            strongest={funnel.strongest_stage}
            testId="client-funnel-mini"
          />
        )}
      </WidgetCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

function ResumenTab({ orgId, primaryCode }) {
  const [withNarrative, setWithNarrative] = useState(false);
  // Default: light /summary endpoint (~12s). Heavy /report (40s+ LLM) sólo cuando
  // el usuario activa "Generar narrativa" — el hook no se dispara hasta entonces.
  const summaryQ = useBusinessSummary(orgId, false);
  const reportQ = useBusinessReport(orgId, { withNarrative: true, enabled: withNarrative });
  const benchmarkQ = useBenchmark(orgId, "funnel_health");
  const showHeavy = withNarrative;
  return (
    <section className="space-y-4">
      <QueryGate query={showHeavy ? reportQ : summaryQ} testId="client-business-summary" loadingLabel={showHeavy ? "Generando narrativa (puede tardar 30-50s)…" : "Cargando resumen ejecutivo (20-30s)…"}>
        {(data) => {
          // /business/summary returns { summary: {...}, narrative_es }
          // /business/report returns { business_score, diagnosis, executive_summary, narrative_es, recommendations, ... }
          const summary = data.summary || {};
          const topActions = summary.do_first || data.recommendations?.top_actions || data.recommendations || [];
          const verdict = summary.verdict || data.business_score?.label || data.executive_summary?.verdict;
          const narrative = data.narrative_es || data.narrative?.text_es || "";
          const whatWorks = summary.what_works || [];
          const whatFails = summary.what_fails || [];
          return (
            <WidgetCard
              title="Resumen Ejecutivo"
              description="Síntesis backend AG-01 · narrativa LLM opcional"
              citations={data.grounding_sources}
              actions={
                <button
                  data-testid="client-toggle-narrative"
                  type="button"
                  onClick={() => setWithNarrative((v) => !v)}
                  className="flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-1 text-[11px] hover:bg-[var(--ag-surface-2)]"
                  disabled={reportQ.isFetching}
                >
                  <Sparkles className="h-3 w-3" />
                  {withNarrative ? "Ocultar narrativa" : "Generar narrativa"}
                </button>
              }
              testId="widget-C4-executive-summary"
            >
              {verdict && (
                <div className="mb-2 flex items-baseline gap-2">
                  <span className="text-2xl font-bold">{summary.score ?? data.business_score?.global ?? "—"}</span>
                  <span className="rounded bg-[var(--ag-surface-2)] px-2 py-0.5 text-[10px] uppercase tracking-wide text-[var(--ag-text-dim)]">
                    {verdict}
                  </span>
                </div>
              )}
              {narrative && (
                <InsightCard
                  title="Narrativa"
                  text={narrative}
                  tone="info"
                  citations={data.narrative?.grounding_sources || data.grounding_sources}
                />
              )}
              {!narrative && !showHeavy && (
                <p className="text-xs text-[var(--ag-text-dim)]">Activa &laquo;Generar narrativa&raquo; para obtener el resumen en lenguaje natural.</p>
              )}
              {(whatWorks.length > 0 || whatFails.length > 0) && (
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {whatWorks.length > 0 && (
                    <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-2 text-xs">
                      <div className="mb-1 font-semibold text-emerald-500">Qué funciona</div>
                      <ul className="space-y-0.5">
                        {whatWorks.slice(0, 4).map((w, i) => (
                          <li key={i} className="text-[var(--ag-text-dim)]" data-testid={`client-works-${i}`}>· {w.label || w}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {whatFails.length > 0 && (
                    <div className="rounded border border-red-500/30 bg-red-500/5 p-2 text-xs">
                      <div className="mb-1 font-semibold text-red-500">Qué falla</div>
                      <ul className="space-y-0.5">
                        {whatFails.slice(0, 4).map((f, i) => (
                          <li key={i} className="text-[var(--ag-text-dim)]" data-testid={`client-fails-${i}`}>· {f.label || f}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {topActions.length > 0 && (
                <div className="mt-3 space-y-1">
                  <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ag-text-dim)]">Top acciones</div>
                  <ul className="space-y-1">
                    {topActions.slice(0, 5).map((a, i) => (
                      <li
                        key={`${a.area || ""}-${i}`}
                        data-testid={`client-top-action-${i}`}
                        className="rounded border border-[var(--ag-border)] px-2 py-1.5 text-xs"
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-[var(--ag-text)]">
                            {a.action || a.title || a.label || (typeof a === "string" ? a : "—")}
                          </span>
                          {a.priority && (
                            <span className="shrink-0 rounded bg-blue-500/15 px-1.5 py-0.5 text-[10px] text-blue-500">{a.priority}</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </WidgetCard>
          );
        }}
      </QueryGate>

      <QueryGate query={benchmarkQ} testId="client-benchmark-bar">
        {(data) => (
          <WidgetCard
            title="Benchmark vs. sector"
            description={`Métrica: ${data.metric} · sector ${data.primary_sector_code || "?"}`}
            citations={data.grounding_sources}
            testId="widget-C16-benchmark-bar"
          >
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold">{data.your_value ?? "—"}</span>
              <BandBadge band={data.your_quartile} label={data.your_quartile || "—"} />
              <span className="text-xs text-[var(--ag-text-dim)]">{data.verdict || ""}</span>
            </div>
          </WidgetCard>
        )}
      </QueryGate>
    </section>
  );
}

function RoadmapTab({ orgId }) {
  const [horizon, setHorizon] = useState("monthly");
  const planQ = useGrowthPlan(orgId, horizon);
  const expQ = useGrowthExperiments(orgId);
  return (
    <section className="space-y-4">
      <WidgetCard
        title="Horizonte del roadmap"
        testId="widget-C5-horizon-picker"
        actions={
          <div className="flex gap-1">
            {HORIZONS.map((h) => (
              <button
                key={h}
                data-testid={`client-roadmap-horizon-${h}`}
                type="button"
                onClick={() => setHorizon(h)}
                className={`rounded border px-2 py-0.5 text-[10px] ${
                  horizon === h
                    ? "border-[var(--ag-text)] bg-[var(--ag-text)] text-[var(--ag-bg)]"
                    : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
                }`}
              >
                {h}
              </button>
            ))}
          </div>
        }
      >
        <QueryGate query={planQ} testId="client-plan">
          {(data) => (
            <>
              <div className="mb-2 flex items-baseline justify-between text-xs">
                <span className="text-[var(--ag-text-dim)]">
                  Esfuerzo estimado: <span className="font-mono text-[var(--ag-text)]">{data.estimated_total_effort_h ?? "—"}h</span>
                </span>
                <span className="text-[var(--ag-text-dim)]">
                  Bloqueados: {data.blocked?.length ?? 0} · Diferidos: {data.deferred?.length ?? 0}
                </span>
              </div>
              <CalendarView calendar={data.calendar} testId="client-roadmap-calendar" />
              {data.expected_outcome && (
                <p className="mt-2 text-[11px] text-[var(--ag-text-dim)]">{data.expected_outcome}</p>
              )}
            </>
          )}
        </QueryGate>
      </WidgetCard>

      <WidgetCard title="Experimentos priorizados" citations={["AG-02"]} testId="widget-C6-experiments">
        <QueryGate query={expQ} testId="client-experiments">
          {(data) => {
            const exps = data.experiments || [];
            if (exps.length === 0) return <EmptyState />;
            return (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {exps.slice(0, 12).map((e) => (
                  <GrowthPlayCard key={e.play_id} experiment={e} />
                ))}
              </div>
            );
          }}
        </QueryGate>
      </WidgetCard>
    </section>
  );
}

function KpisTab({ orgId }) {
  const kpisQ = useGrowthKpis(orgId);
  const forecastQ = useGrowthForecast(orgId, "monthly");
  return (
    <section className="space-y-4">
      <QueryGate query={kpisQ} testId="client-kpis">
        {(data) => (
          <WidgetCard
            title="KPIs"
            description="10 indicadores primarios · backend determinístico"
            citations={data.grounding_sources}
            testId="widget-C7-kpis"
          >
            <WidgetGrid cols="lg:grid-cols-3">
              {(data.kpis || []).map((kpi) => (
                <KpiCard key={kpi.code} kpi={kpi} />
              ))}
            </WidgetGrid>
            {Array.isArray(data.forbidden_metrics) && data.forbidden_metrics.length > 0 && (
              <p className="mt-3 text-[10px] text-[var(--ag-text-dim)]">
                Métricas no calculadas (contrato anti-€): {data.forbidden_metrics.join(", ")}
              </p>
            )}
          </WidgetCard>
        )}
      </QueryGate>

      <QueryGate query={forecastQ} testId="client-forecast">
        {(data) => (
          <WidgetCard
            title="Forecast"
            description="Bandas relativas · sin proyecciones monetarias"
            citations={data.grounding_sources}
            testId="widget-C8-forecast"
          >
            <div className="flex flex-wrap items-center gap-3">
              <MetricCard label="Estado actual" value={data.current_state?.funnel_health ?? "—"} hint="funnel" />
              <MetricCard
                label="Proyectado"
                value={data.projected_state_if_plan_executed?.funnel_health ?? "—"}
                hint={`horizonte: ${data.horizon}`}
              />
              <BandBadge band={data.delta_estimate?.band || "0"} label={`Δ ${data.delta_estimate?.label || "indeterminado"}`} />
              <span className="text-xs text-[var(--ag-text-dim)]">{data.confidence_label}</span>
            </div>
            {Array.isArray(data.caveats) && data.caveats.length > 0 && (
              <ul className="mt-3 space-y-1 text-[11px] text-[var(--ag-text-dim)]">
                {data.caveats.slice(0, 5).map((c, i) => (
                  <li key={i}>· {c}</li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-[10px] text-[var(--ag-text-dim)]">
              {data.monetary_projections}
            </p>
          </WidgetCard>
        )}
      </QueryGate>
    </section>
  );
}

function CompetenciaTab({ orgId }) {
  const oppQ = useCompetitorOpportunities(orgId);
  const candQ = useCompetitorCandidates(orgId);
  const [competitorId, setCompetitorId] = useState(null);
  const swotQ = useCompetitorSwot(orgId, competitorId);
  const gapQ = useCompetitorGap(orgId, competitorId);

  return (
    <section className="space-y-4">
      <WidgetCard title="Candidatos competitivos" citations={["AG-05"]} testId="widget-C9a-candidates">
        <QueryGate query={candQ} testId="client-candidates">
          {(data) => {
            const cands = data.candidates || [];
            if (cands.length === 0) return <EmptyState />;
            return (
              <ul className="space-y-1">
                {cands.slice(0, 10).map((c) => (
                  <li
                    key={c.org_id || c.id}
                    data-testid={`competitor-cand-${c.org_id || c.id}`}
                    className="flex items-center justify-between rounded border border-[var(--ag-border)] px-2 py-1.5 text-xs"
                  >
                    <span>
                      {c.name || c.org_id}{" "}
                      <span className="text-[var(--ag-text-dim)]">· {c.city || "—"}</span>
                    </span>
                    <button
                      data-testid={`competitor-select-${c.org_id || c.id}`}
                      type="button"
                      onClick={() => setCompetitorId(c.org_id || c.id)}
                      className={`rounded border px-2 py-0.5 text-[10px] ${
                        competitorId === (c.org_id || c.id)
                          ? "border-[var(--ag-text)] bg-[var(--ag-text)] text-[var(--ag-bg)]"
                          : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
                      }`}
                    >
                      {competitorId === (c.org_id || c.id) ? "✓ Seleccionado" : "Comparar"}
                    </button>
                  </li>
                ))}
              </ul>
            );
          }}
        </QueryGate>
      </WidgetCard>

      {competitorId && (
        <QueryGate query={swotQ} testId="client-swot">
          {(data) => (
            <WidgetCard title="SWOT vs. competidor" citations={data.grounding_sources} testId="widget-C9-swot">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <CompetitorCard title="Fortalezas" items={data.strengths || data.swot?.strengths} tone="success" />
                <CompetitorCard title="Debilidades" items={data.weaknesses || data.swot?.weaknesses} tone="warning" />
                <CompetitorCard title="Oportunidades" items={data.opportunities || data.swot?.opportunities} tone="info" />
                <CompetitorCard title="Amenazas" items={data.threats || data.swot?.threats} tone="danger" />
              </div>
            </WidgetCard>
          )}
        </QueryGate>
      )}

      {competitorId && (
        <QueryGate query={gapQ} testId="client-gap">
          {(data) => (
            <WidgetCard title="Gap analysis vs. competidor" citations={data.grounding_sources} testId="widget-C10-gap">
              <RankedList
                items={data.gaps || data.items || []}
                emptyMessage="Información insuficiente."
                renderRight={(it) => it.delta ?? it.gap ?? ""}
              />
            </WidgetCard>
          )}
        </QueryGate>
      )}

      <QueryGate query={oppQ} testId="client-opportunities">
        {(data) => (
          <WidgetCard title="Oportunidades detectadas" citations={data.grounding_sources} testId="widget-C9b-opps">
            <RankedList items={data.opportunities || data.items || []} emptyMessage="Información insuficiente." />
          </WidgetCard>
        )}
      </QueryGate>
    </section>
  );
}

function SeoTab({ primaryCode, allCodes }) {
  const [selectedCode, setSelectedCode] = useState(primaryCode);
  const scoreQ = useSeoScore(selectedCode);
  const linksQ = useSeoLinks(selectedCode);
  return (
    <section className="space-y-4">
      <WidgetCard title="Código analizado" testId="widget-C11-code-picker">
        <div className="flex flex-wrap gap-1.5">
          {(allCodes || []).map((c) => (
            <button
              key={c}
              data-testid={`client-seo-code-${c}`}
              type="button"
              onClick={() => setSelectedCode(c)}
              className={`rounded border px-2 py-1 text-xs ${
                selectedCode === c
                  ? "border-[var(--ag-text)] bg-[var(--ag-text)] text-[var(--ag-bg)]"
                  : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
              }`}
            >
              {c}
            </button>
          ))}
          {(!allCodes || allCodes.length === 0) && (
            <span className="text-xs text-[var(--ag-text-dim)]">Información insuficiente.</span>
          )}
        </div>
      </WidgetCard>

      <QueryGate query={scoreQ} testId="client-seo-score">
        {(data) => {
          const components = data.components || {};
          const items = Object.entries(components).map(([k, v]) => ({
            code: k.toUpperCase(),
            score: typeof v === "number" ? v : v?.score,
            name: k,
            label: v?.label,
          }));
          return (
            <WidgetCard title={`SEO Score · ${data.code || selectedCode}`} citations={data.grounding_sources} testId="widget-C11-seo-score">
              <div className="mb-3 flex items-baseline gap-3">
                <span className="text-3xl font-bold">{data.score ?? "—"}</span>
                <span className="text-xs text-[var(--ag-text-dim)]">{data.label || data.verdict || ""}</span>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
                {items.map((it) => (
                  <SeoCard key={it.code} item={it} />
                ))}
              </div>
            </WidgetCard>
          );
        }}
      </QueryGate>

      <QueryGate query={linksQ} testId="client-seo-links">
        {(data) => (
          <WidgetCard title="Oportunidades de link interno" citations={data.grounding_sources} testId="widget-C12-link-opps">
            <RankedList
              items={(data.recommendations || data.links || []).map((l) => ({
                code: l.target_code || l.code,
                label: l.anchor_text || l.label || "",
                value: l.priority || l.relation || "",
              }))}
              emptyMessage="Información insuficiente."
            />
          </WidgetCard>
        )}
      </QueryGate>
    </section>
  );
}

function ContentTab({ primaryCode, allCodes }) {
  const [selectedCode, setSelectedCode] = useState(primaryCode);
  const [contentType, setContentType] = useState("landing");
  const previewQ = useContentPreview(selectedCode, contentType);
  const TYPES = ["landing", "blog", "email", "social", "faq"];
  return (
    <section className="space-y-4">
      <WidgetCard
        title="Contenido sugerido"
        description="Previsualización determinística por código · sin escritura"
        testId="widget-C13-content-picker"
      >
        <div className="mb-2 flex flex-wrap gap-1.5">
          {(allCodes || []).map((c) => (
            <button
              key={c}
              data-testid={`client-content-code-${c}`}
              type="button"
              onClick={() => setSelectedCode(c)}
              className={`rounded border px-2 py-1 text-xs ${
                selectedCode === c
                  ? "border-[var(--ag-text)] bg-[var(--ag-text)] text-[var(--ag-bg)]"
                  : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TYPES.map((t) => (
            <button
              key={t}
              data-testid={`client-content-type-${t}`}
              type="button"
              onClick={() => setContentType(t)}
              className={`rounded border px-2 py-0.5 text-[10px] ${
                contentType === t
                  ? "border-[var(--ag-text)] bg-[var(--ag-text)] text-[var(--ag-bg)]"
                  : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </WidgetCard>

      <QueryGate query={previewQ} testId="client-content-preview">
        {(data) => (
          <WidgetCard
            title={`Previsualización · ${data.content_type || contentType} · ${data.code || selectedCode}`}
            citations={data.grounding_sources}
            testId="widget-C13-content-preview"
          >
            <InsightCard
              title={data.title || data.headline || ""}
              text={data.body || data.text || data.description || JSON.stringify(data.preview || data, null, 2).slice(0, 800)}
              tone="info"
              citations={data.citations}
            />
            {data.cta && (
              <ContentCard item={{ code: data.code, title: data.cta.text || data.cta, reason: data.cta.reason }} />
            )}
          </WidgetCard>
        )}
      </QueryGate>
    </section>
  );
}

function BenchmarkTab({ orgId, primaryCode }) {
  const metricOptions = [
    { id: "funnel_health", label: "Funnel Health" },
    { id: "business_score", label: "Business Score" },
    { id: "catalog_count", label: "Catálogo" },
    { id: "rating_avg", label: "Rating ★" },
    { id: "rating_count", label: "# Reseñas" },
  ];
  const [metric, setMetric] = useState("funnel_health");
  const benchmarkQ = useBenchmark(orgId, metric);
  const sectorQ = useSectorIntelligence(primaryCode);
  return (
    <section className="space-y-4">
      <WidgetCard title="Métrica a comparar" testId="widget-C14-metric-picker">
        <div className="flex flex-wrap gap-1.5">
          {metricOptions.map((m) => (
            <button
              key={m.id}
              data-testid={`client-benchmark-metric-${m.id}`}
              type="button"
              onClick={() => setMetric(m.id)}
              className={`rounded border px-2 py-1 text-xs ${
                metric === m.id
                  ? "border-[var(--ag-text)] bg-[var(--ag-text)] text-[var(--ag-bg)]"
                  : "border-[var(--ag-border)] hover:bg-[var(--ag-surface-2)]"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </WidgetCard>

      <QueryGate query={benchmarkQ} testId="client-percentile-gauge">
        {(data) => (
          <WidgetCard
            title="Posición percentil · vs. tu sector"
            description={data.verdict}
            citations={data.grounding_sources}
            testId="widget-C14-percentile"
          >
            <PercentileGauge
              percentile={data.your_percentile}
              quartile={data.your_quartile}
              n={data.sector_aggregates?.n_orgs_compared}
            />
          </WidgetCard>
        )}
      </QueryGate>

      <QueryGate query={sectorQ} testId="client-sector-bars">
        {(data) => (
          <WidgetCard
            title={`Sector aggregates · ${data.sector_code || primaryCode}`}
            description={`N=${data.totals?.orgs_in_sector ?? "?"} orgs · anonimizado por privacy contract`}
            citations={data.grounding_sources}
            testId="widget-C15-sector-bars"
          >
            <SectorBars deciles={data.deciles} testId="client-sector-bars" />
          </WidgetCard>
        )}
      </QueryGate>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page shell · org resolver
// ---------------------------------------------------------------------------

function OrgPicker({ onPick }) {
  const q = useSuperadminOrgs("", 50, true);
  return (
    <div className="mx-auto max-w-2xl space-y-3 p-4">
      <h2 className="text-lg font-semibold">Selecciona una organización</h2>
      <p className="text-xs text-[var(--ag-text-dim)]">
        La ruta requiere un slug · elige cualquier centro publicado para abrir su Advisor.
      </p>
      <QueryGate query={q} testId="client-org-picker">
        {(data) => {
          const orgs = (data?.items || data?.orgs || data || []).slice(0, 30);
          return (
            <ul className="space-y-1">
              {orgs.map((o) => (
                <li
                  key={o.slug || o.id}
                  data-testid={`org-pick-${o.slug || o.id}`}
                  className="rounded border border-[var(--ag-border)] px-3 py-2 text-sm hover:bg-[var(--ag-surface-2)]"
                >
                  <button type="button" onClick={() => onPick(o.slug)} className="flex w-full items-center justify-between">
                    <span className="text-[var(--ag-text)]">
                      <Building2 className="mr-1 inline h-3 w-3" />
                      {o.name || o.slug}
                    </span>
                    <span className="text-xs text-[var(--ag-text-dim)]">{o.city || "—"} · {o.slug}</span>
                  </button>
                </li>
              ))}
            </ul>
          );
        }}
      </QueryGate>
    </div>
  );
}

export default function ClientAdvisorDashboard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const activeTab = search.get("tab") || "resumen";
  const setTab = (t) => {
    const next = new URLSearchParams(search);
    next.set("tab", t);
    setSearch(next, { replace: true });
  };

  // All hooks MUST run unconditionally · rules-of-hooks
  const orgQ = useOrgBySlug(slug);
  const org = orgQ.data?.org;
  const orgId = org?.id;
  const allCodes = useMemo(() => {
    const codes = org?.taxonomy_codes || [];
    return Array.from(new Set([...codes].filter(Boolean)));
  }, [org]);
  const primaryCode = allCodes[0] || null;
  const businessScoreQ = useBusinessScore(orgId);
  const growthFunnelQ = useGrowthFunnel(orgId);

  // If no slug provided, render an org picker (founder UX)
  if (!slug) {
    return (
      <div className="min-h-screen bg-[var(--ag-bg)] text-[var(--ag-text)]" data-testid="client-advisor-no-slug">
        <header className="border-b border-[var(--ag-border)] bg-[var(--ag-bg)]/95 px-4 py-3">
          <Link to="/founder" className="flex items-center gap-1 text-xs text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]">
            <ArrowLeft className="h-3.5 w-3.5" /> Founder
          </Link>
          <h1 className="mt-1 text-lg font-semibold">Client Advisor · selecciona organización</h1>
        </header>
        <OrgPicker onPick={(s) => navigate(`/centro/advisor/${s}`)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--ag-bg)] text-[var(--ag-text)]" data-testid="client-advisor-dashboard">
      <header className="sticky top-0 z-20 border-b border-[var(--ag-border)] bg-[var(--ag-bg)]/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-3">
          <Link
            to="/founder/advisor"
            data-testid="client-advisor-back"
            className="flex items-center gap-1 text-xs text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Founder Advisor
          </Link>
          <div className="flex-1">
            <h1 className="text-lg font-semibold tracking-tight" data-testid="client-advisor-title">
              {org?.name || slug}
            </h1>
            <p className="text-[11px] text-[var(--ag-text-dim)]">
              Client Advisor Dashboard · {org?.city || "—"} · códigos: {allCodes.join(", ") || "—"}
            </p>
          </div>
          <button
            data-testid="client-advisor-refresh"
            type="button"
            onClick={() => {
              orgQ.refetch();
              businessScoreQ.refetch();
              growthFunnelQ.refetch();
            }}
            className="flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
          >
            <RefreshCw className={`h-3 w-3 ${businessScoreQ.isFetching ? "animate-spin" : ""}`} />
            Refrescar
          </button>
        </div>
        <nav className="border-t border-[var(--ag-border)] bg-[var(--ag-surface)]/40">
          <div className="mx-auto flex max-w-6xl flex-wrap gap-1 overflow-x-auto px-4 py-1.5">
            {CLIENT_TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`client-advisor-tab-${t.id}`}
                type="button"
                onClick={() => setTab(t.id)}
                className={`shrink-0 rounded px-3 py-1 text-xs ${
                  activeTab === t.id
                    ? "bg-[var(--ag-text)] text-[var(--ag-bg)]"
                    : "text-[var(--ag-text-dim)] hover:bg-[var(--ag-surface-2)]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-4">
        {orgQ.isLoading && <LoadingState label="Resolviendo organización…" />}
        {orgQ.error && <EmptyState title="Centro no encontrado" message="Revisa el slug del enlace." />}
        {orgId && (
          <>
            <ClientHero org={org} businessQuery={businessScoreQ} funnelQuery={growthFunnelQ} />
            <div className="mt-4">
              {activeTab === "resumen" && <ResumenTab orgId={orgId} primaryCode={primaryCode} />}
              {activeTab === "roadmap" && <RoadmapTab orgId={orgId} />}
              {activeTab === "kpis" && <KpisTab orgId={orgId} />}
              {activeTab === "competencia" && <CompetenciaTab orgId={orgId} />}
              {activeTab === "seo" && <SeoTab primaryCode={primaryCode} allCodes={allCodes} />}
              {activeTab === "content" && <ContentTab primaryCode={primaryCode} allCodes={allCodes} />}
              {activeTab === "benchmark" && (
                <BenchmarkTab orgId={orgId} primaryCode={primaryCode} />
              )}
              <PrioritiesRoadmapRow orgId={orgId} />
            </div>
          </>
        )}
        <footer className="mt-6 border-t border-[var(--ag-border)] pt-3">
          <CitationBadge sources={["AG-01", "AG-02", "AG-03", "AG-04", "AG-05", "AG-06", "ai_platform_freeze=true"]} testId="client-advisor-footer" />
        </footer>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tail row · C17 PriorityMatrix + C18 RoadmapTimeline (always visible)
// ---------------------------------------------------------------------------

function PrioritiesRoadmapRow({ orgId }) {
  const prQ = useBusinessPriorities(orgId);
  const rdQ = useBusinessRoadmap(orgId);
  return (
    <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
      <QueryGate query={prQ} testId="client-priorities" loadingLabel="Calculando prioridades (20-40s)…">
        {(data) => {
          // Backend AG-01 returns priority_matrix: flat list of actions sorted by priority/impact.
          const items = data.priority_matrix || data.priorities || data.items || [];
          return (
            <WidgetCard
              title="Priority Matrix"
              description={`Total acciones: ${data.total_actions ?? items.length}`}
              citations={data.grounding_sources}
              testId="widget-C17-priorities"
            >
              {items.length === 0 ? (
                <EmptyState />
              ) : (
                <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {items.slice(0, 8).map((p, i) => (
                    <li
                      key={`${p.area || ""}-${p.action || i}`}
                      data-testid={`priority-${i}`}
                      className="rounded border border-[var(--ag-border)] px-2 py-1.5 text-xs"
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-medium text-[var(--ag-text)]">
                          {p.action || p.title || p.label || `Prioridad ${i + 1}`}
                        </span>
                        <div className="flex items-center gap-1 shrink-0">
                          {p.priority && (
                            <span className="rounded bg-blue-500/15 px-1.5 py-0.5 text-[10px] text-blue-500">{p.priority}</span>
                          )}
                          {p.impact_label && (
                            <span className="rounded bg-[var(--ag-surface-2)] px-1.5 py-0.5 text-[10px]">{p.impact_label}</span>
                          )}
                        </div>
                      </div>
                      <div className="mt-1 flex items-center justify-between text-[10px] text-[var(--ag-text-dim)]">
                        <span>{p.area || ""}</span>
                        {p.impact_score != null && <span className="font-mono">impact: {p.impact_score}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </WidgetCard>
          );
        }}
      </QueryGate>

      <QueryGate query={rdQ} testId="client-roadmap-timeline" loadingLabel="Generando roadmap (20-40s)…">
        {(data) => {
          // Backend AG-01 returns horizons: { quick_wins: [...], "30_days": [...], "90_days": [...], "180_days": [...] }
          const horizons = data.horizons || {};
          const labels = {
            quick_wins: "Quick wins",
            "30_days": "30 días",
            "90_days": "90 días",
            "180_days": "180 días",
          };
          const order = ["quick_wins", "30_days", "90_days", "180_days"];
          const sections = order
            .map((k) => ({ key: k, label: labels[k], items: horizons[k] || [] }))
            .filter((s) => s.items.length > 0);
          return (
            <WidgetCard
              title="Roadmap Timeline"
              description={`${sections.length} horizontes · ${data.total_actions ?? "?"} acciones`}
              citations={data.grounding_sources}
              testId="widget-C18-roadmap-timeline"
            >
              {sections.length === 0 ? (
                <EmptyState />
              ) : (
                <div className="space-y-2">
                  {sections.map((s) => (
                    <div key={s.key} data-testid={`roadmap-horizon-${s.key}`}>
                      <div className="mb-1 flex items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--ag-text-dim)]">{s.label}</span>
                        <span className="text-[10px] text-[var(--ag-text-dim)]">({s.items.length})</span>
                      </div>
                      <ol className="space-y-1">
                        {s.items.slice(0, 4).map((r, i) => (
                          <li
                            key={`${s.key}-${i}`}
                            data-testid={`roadmap-${s.key}-${i}`}
                            className="rounded border border-[var(--ag-border)] px-2 py-1.5 text-xs"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{r.action || r.title || r.label}</span>
                              {r.priority && (
                                <span className="rounded bg-blue-500/15 px-1.5 py-0.5 text-[10px] text-blue-500">{r.priority}</span>
                              )}
                            </div>
                            {r.rationale && <p className="mt-1 text-[var(--ag-text-dim)]">{r.rationale}</p>}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
              )}
            </WidgetCard>
          );
        }}
      </QueryGate>
    </div>
  );
}
